import React, { useEffect } from 'react';
import { useRouteMatch, generatePath, Route, Switch } from 'react-router-dom';
import { ViewList as ViewListIcon, DirectionsCar as DirectionsCarIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Box, Typography } from '@mui/material';
import routes from '../../../../routes';
import RescueMobileCard from '../../../Rescue/RescueMobileCard';
import useRescuerDashboardActions from '../../../../hooks/useRescuerDashboardActions';
import MyRescuesMobile from './myRescues/MyRescuesMobile';
import useActiveUser from '../../../../hooks/useActiveUser';
import { getMyRescues, getRecentUnclosedRescues } from '../../../../helpers/RescuesHelper';
import MobileWrapper from '../../../../components/Common/MobileBottomNavWrapper';
import RecentRescuesMobile from './RecentRescues/RecentRescuesMobile';
import MyRescuesBreadcrumb from './Components/MyRescuesBreadcrumb';
import useHasActiveFeatureFlag from '../../../../hooks/useFeatureFlags';
import { FF_REQUIRE_FOOD_SAFETY_COURSE_COMPLETION } from '../../../../components/FeatureFlagWrapper';
import FoodSafetyCourseConfirmationDialog from '../../../../components/FoodSafetyCourseConfirmationDialog';

export const RESCUER_MOBILE_DASHBOARD_TAB_MY_RESCUES = 'my-rescues';
export const RESCUER_MOBILE_DASHBOARD_TAB_RECENT_RESCUES = 'recent-rescues';

export const paths = [
  generatePath(routes.mobileDashboardMyRescues, { tab: RESCUER_MOBILE_DASHBOARD_TAB_MY_RESCUES }),
  generatePath(routes.mobileDashboardMyRescues, { tab: RESCUER_MOBILE_DASHBOARD_TAB_RECENT_RESCUES }),
];

export function RescuesLoader({ highlighted, rows }) {
  return (
    <Box display="grid" rowGap={2} width="100%">
      {[...Array(rows || 10).keys()].map((index) => (
        <RescueMobileCard key={index} highlighted={highlighted} isLoading />
      ))}
    </Box>
  );
}

export function NoRescueCard({ text }) {
  return (
    <Box
      boxShadow={25}
      p={2}
      sx={{
        width: '100%',
        borderRadius: 16,
        backgroundColor: '#fff',
        position: 'relative',
      }}
    >
      <Typography color="textSecondary" variant="body1">
        {text}
      </Typography>
    </Box>
  );
}

const RescuerMyRescuesDashboardMobile = () => {
  const { url } = useRouteMatch();
  const activeUser = useActiveUser();
  const rescuesEntities = useSelector((state) => state.entities.rescues);
  const featureFlagsEntities = useSelector((state) => state.entities.featureFlags);
  const { formatMessage } = useIntl();
  const coursesEntities = useSelector((state) => state.entities.users.courses);
  const courses = coursesEntities.byUserId[activeUser.id] || [];
  const coursesCompleted =
    activeUser?.courses_status === 'Completed' ||
    (courses.length > 0 &&
      courses.every((course) =>
        (course?.modules || []).length > 0 ? course.modules.every((module) => module.completed) : false
      ));
  const hideFoodSafetyDialog = useSelector(state => state.ui.persisted.hideFoodSafetyDialog);
  const hasActiveRequireFoodSafetyFF = useHasActiveFeatureFlag(FF_REQUIRE_FOOD_SAFETY_COURSE_COMPLETION);
  const showFoodSafetyDialog = !coursesCompleted && (hasActiveRequireFoodSafetyFF || !hideFoodSafetyDialog);

  const upcomingRescuesCount = getMyRescues(activeUser.id, rescuesEntities).length;
  const recentRescuesCount = getRecentUnclosedRescues(activeUser.id, rescuesEntities).length;

  const { fetchMyFrusTab } = useRescuerDashboardActions();

  const getActiveTab = () => {
    const path = paths.indexOf(url);

    return Math.max(0, path); // no negatives numbers
  };

  useEffect(() => {
    if (url !== paths[0] && url !== paths[1]) {
      // my frus & my rescues
      fetchMyFrusTab(false, true); // quietMode, force
    }
  }, [fetchMyFrusTab]);

  if (featureFlagsEntities.inflight) {
    return null;
  }

  const bottomTabs = [
    {
      label: formatMessage({ id: 'rescuer-my-rescues-mobile.bottom-navigation.upcoming' }),
      url: paths[0],
      icon: <ViewListIcon sx={{ fontSize: '1.8rem' }} />,
      badgeCounter: upcomingRescuesCount,
      testId: 'rescuerDesktopTab0',
    },
    {
      label: formatMessage({ id: 'rescuer-my-rescues-mobile.bottom-navigation.recent' }),
      url: paths[1],
      icon: <DirectionsCarIcon sx={{ fontSize: '1.8rem' }} />,
      badgeCounter: recentRescuesCount,
      testId: 'rescuerDesktopTab1',
    },
  ];

  return (
    <>
      {showFoodSafetyDialog && <FoodSafetyCourseConfirmationDialog isMobile />}

      <MobileWrapper
        containerId="rescuer-dashboard-mobile-div"
        breadcrumb={<MyRescuesBreadcrumb />}
        activeTab={getActiveTab()}
        bottomTabs={bottomTabs}
        pageContent={
          <Switch>
            <Route path={[generatePath(routes.mobileDashboardMyRescues), paths[0]]} exact component={MyRescuesMobile} />
            <Route path={paths[1]} exact component={RecentRescuesMobile} />
          </Switch>
        }
      />
    </>
  );
};

export default RescuerMyRescuesDashboardMobile;
